import React from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Box, Typography } from '@mui/material';
import { STUDENT_ERROR_TEXT, STUDENT_GREEN, STUDENT_INTER, STUDENT_NAVY, STUDENT_SLATE, STUDENT_SUCCESS_TEXT } from '../style/verbaScholaStudentStyles';
import { AudioApi } from '../api/AudioApi';
import { IStudentCheckReview } from '../api/VerbaScholaApi';
import { StudentStepCardProps } from './VerbaScholaStudentStepShared';
import { recordStudentActivity } from './studentActivityTelemetry';

export interface StudentCheckProps extends StudentStepCardProps {
    onCheckUpdate?: (update: { failed_attempts?: number; review_result?: IStudentCheckReview }) => void;
    ready: boolean;
    advanceLabel: string;
    onAdvance: () => void;
    onPrevious?: () => void;
    onExit: () => void;
    onSkipComplete: () => void;
}

/** The inline outcome of a check, in the semantic text color (never an Alert box). */
export function StudentCheckFeedback({ correct, neutral = false, children, focusRef }: { correct: boolean; neutral?: boolean; children: React.ReactNode; focusRef?: React.Ref<HTMLParagraphElement> }) {
    return <Typography ref={focusRef} tabIndex={-1} role="status" sx={{ fontFamily: STUDENT_INTER, fontSize: 13, fontWeight: 600, color: neutral ? STUDENT_NAVY : correct ? STUDENT_SUCCESS_TEXT : STUDENT_ERROR_TEXT }}>{children}</Typography>;
}

/** The checked result on a choice or match tile: never color alone (style guide §5). */
export function StudentChoiceResult({ correct }: { correct: boolean }) {
    return <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: .5, fontFamily: STUDENT_INTER, fontSize: 12, fontWeight: 600, color: correct ? STUDENT_SUCCESS_TEXT : STUDENT_ERROR_TEXT }}>
        {correct && <CheckCircleRoundedIcon aria-hidden sx={{ fontSize: 16, color: STUDENT_GREEN }} />}{correct ? 'Correct' : 'Try again'}
    </Box>;
}

export function StudentCheckReviewNotice() {
    return <Typography sx={{ fontFamily: STUDENT_INTER, fontSize: 13, color: STUDENT_SLATE }}>Reviewing completed work. Your saved progress won’t change.</Typography>;
}

export function shuffleChecks<T>(items: readonly T[]): T[] {
    const result = [...items];
    for (let index = result.length - 1; index > 0; index--) {
        const other = Math.floor(Math.random() * (index + 1));
        [result[index], result[other]] = [result[other], result[index]];
    }
    return result;
}

/** One owned audio channel per activity; only ended grants listening evidence. */
export function useStudentCheckAudio() {
    const [current, setCurrent] = React.useState<number | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<number | null>(null);
    const [heard, setHeard] = React.useState<Set<number>>(() => new Set());
    const owned = React.useRef<HTMLAudioElement | null>(null);
    const url = React.useRef<string | null>(null);
    const timer = React.useRef<ReturnType<typeof setTimeout>>();
    const generation = React.useRef(0);
    const active = React.useRef(false);
    const stop = React.useCallback(() => {
        if (timer.current) clearTimeout(timer.current);
        if (owned.current) { owned.current.onended = null; owned.current.onerror = null; owned.current.pause(); }
        owned.current = null;
        if (url.current) URL.revokeObjectURL(url.current);
        url.current = null;
    }, []);
    React.useEffect(() => () => { generation.current++; active.current = false; stop(); }, [stop]);
    const play = async (sentenceId: number, speaker?: string | null, onEnded?: () => void) => {
        if (active.current) return;
        active.current = true;
        const request = ++generation.current;
        setCurrent(sentenceId); setLoading(true); setError(null);
        const fail = () => {
            if (generation.current !== request) return;
            generation.current++; active.current = false; stop(); setCurrent(null); setLoading(false); setError(sentenceId);
        };
        timer.current = setTimeout(fail, 20000);
        try {
            speaker = speaker?.trim().toUpperCase();
            const response = await (speaker === 'A' ? AudioApi.getSpeakerA(sentenceId) : speaker === 'B' ? AudioApi.getSpeakerB(sentenceId) : AudioApi.getTeacherSentence(sentenceId, false));
            if (generation.current !== request) return;
            stop(); url.current = URL.createObjectURL(response.data);
            const clip = new Audio(url.current); owned.current = clip;
            clip.onerror = fail;
            clip.onended = () => {
                if (generation.current !== request) return;
                generation.current++; active.current = false; stop(); setCurrent(null); setLoading(false);
                setHeard(previous => new Set(previous).add(sentenceId));
                onEnded?.();
            };
            timer.current = setTimeout(fail, 120000);
            await clip.play();
            if (generation.current === request) recordStudentActivity('model_audio_play');
            if (generation.current === request) setLoading(false);
        } catch { fail(); }
    };
    return { current, loading, error, heard, play, cancel: () => { generation.current++; active.current = false; stop(); setCurrent(null); setLoading(false); } };
}
